// v3.7.2 — Salle Ops Live : salle de contrôle owner (mobile-first).
// Suivi temps réel des livreurs (carte + liste) et réassignation des courses.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const NEON = "#2EE27B";
const POLL_MS = 5000;

function _agoLabel(idleS) {
  if (idleS === null || idleS === undefined) return "—";
  if (idleS < 60) return `il y a ${idleS}s`;
  if (idleS < 3600) return `il y a ${Math.round(idleS / 60)} min`;
  return `il y a ${Math.round(idleS / 3600)} h`;
}

function _waitLabel(s) {
  if (s === null || s === undefined) return "";
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)} min ${String(s % 60).padStart(2, "0")}s`;
}

const ORDER_PILLS = {
  pending_acceptance: { txt: "📡 En attente livreur", color: "#FBBF24", bg: "rgba(251,191,36,0.14)" },
  accepted: { txt: "🛵 Acceptée", color: "#60A5FA", bg: "rgba(96,165,250,0.14)" },
  picked_up: { txt: "📦 Colis récupéré", color: "#60A5FA", bg: "rgba(96,165,250,0.18)" },
  delivering: { txt: "🚀 En livraison", color: "#34D399", bg: "rgba(52,211,153,0.14)" },
  delivered: { txt: "✓ Livrée", color: "#34D399", bg: "rgba(52,211,153,0.22)" },
};

// ── Mini-carte Mapbox : positions livreurs (vert = dispo, ambre = en course) ─
function OpsMiniMap({ couriers, orders }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const token = process.env.REACT_APP_MAPBOX_TOKEN;
    if (!token) return;
    mapboxgl.accessToken = token;
    mapRef.current = new mapboxgl.Map({
      container: containerRef.current,
      style: "mapbox://styles/mapbox/dark-v11",
      center: [-3.99, 5.36],
      zoom: 11.2,
      attributionControl: false,
    });
    return () => { try { mapRef.current?.remove(); } catch (_) {} mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];
    const pts = [];
    couriers.forEach((c) => {
      if (!Number.isFinite(c.lat) || !Number.isFinite(c.lng)) return;
      const el = document.createElement("div");
      const busy = !!c.busy_order_ref;
      el.style.cssText = `width:16px;height:16px;border-radius:50%;background:${busy ? "#FBBF24" : NEON};border:2.5px solid #04150D;box-shadow:0 0 12px ${busy ? "rgba(251,191,36,0.7)" : "rgba(46,226,123,0.7)"};`;
      const mk = new mapboxgl.Marker({ element: el })
        .setLngLat([c.lng, c.lat])
        .setPopup(new mapboxgl.Popup({ offset: 12, closeButton: false }).setHTML(
          `<div style="font:600 12px Inter,sans-serif;color:#0F172A">🛵 ${c.courier_name}<br/>${busy ? "En course " + c.busy_order_ref : "Disponible"}</div>`
        ))
        .addTo(map);
      markersRef.current.push(mk);
      pts.push([c.lng, c.lat]);
    });
    orders.forEach((o) => {
      const d = o.drop || {};
      if (!Number.isFinite(d.lat) || !Number.isFinite(d.lng)) return;
      const el = document.createElement("div");
      el.style.cssText = "width:11px;height:11px;border-radius:3px;background:#F87171;border:2px solid #04150D;transform:rotate(45deg);box-shadow:0 0 8px rgba(248,113,113,0.6);";
      const mk = new mapboxgl.Marker({ element: el }).setLngLat([d.lng, d.lat]).addTo(map);
      markersRef.current.push(mk);
      pts.push([d.lng, d.lat]);
    });
    if (pts.length > 1) {
      const b = pts.reduce((bb, p) => bb.extend(p), new mapboxgl.LngLatBounds(pts[0], pts[0]));
      try { map.fitBounds(b, { padding: 46, maxZoom: 13.5, duration: 600 }); } catch (_) {}
    }
  }, [couriers, orders]);

  return <div ref={containerRef} data-testid="ops-live-map" style={{ width: "100%", height: 230, borderRadius: 16, overflow: "hidden", border: "1px solid rgba(46,226,123,0.2)" }} />;
}

// ── Bottom-sheet de réassignation ────────────────────────────────────────────
function ReassignSheet({ order, couriers, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const candidates = couriers.filter(
    (c) => c.online && c.session_id !== order.courier_id && c.session_id !== order.current_candidate_session_id
  );
  const reassign = async (target) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/v3/delivery/ops/orders/${order.order_ref}/reassign`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` },
        body: JSON.stringify({ target_session_id: target.session_id }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        toast.success(`🔁 ${order.order_ref} proposée à ${d.target_name}`);
        onDone();
      } else {
        toast.error(d.error || "Réassignation impossible");
      }
    } catch (_) { toast.error("Erreur réseau"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="ops-reassign-sheet" onClick={onClose} style={sheetOverlayStyle}>
      <div onClick={(e) => e.stopPropagation()} style={sheetStyle}>
        <div style={{ width: 40, height: 4, borderRadius: 999, background: "rgba(255,255,255,0.2)", margin: "0 auto 14px" }} />
        <div style={{ fontSize: 12, color: NEON, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase" }}>🔁 Réassigner la course</div>
        <div style={{ fontFamily: "monospace", fontSize: 13, color: "#8FA3A3", marginTop: 4 }}>{order.order_ref}</div>
        <div style={{ fontSize: 12.5, color: "#EAFDF3", marginTop: 6 }}>
          {order.commerce_name} → {order.drop?.description || order.drop?.address_civ || "client"}
          {order.courier_name && <span style={{ color: "#FBBF24" }}> · actuellement : {order.courier_name}</span>}
        </div>
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8, maxHeight: "42vh", overflowY: "auto" }}>
          {candidates.length === 0 && (
            <div style={{ padding: "22px 10px", textAlign: "center", color: "#8FA3A3", fontSize: 13 }}>Aucun autre livreur en ligne pour le moment.</div>
          )}
          {candidates.map((c) => (
            <button key={c.session_id} onClick={() => reassign(c)} disabled={busy}
              data-testid={`ops-reassign-target-${c.session_id.slice(0, 8)}`} style={candidateBtnStyle}>
              <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: c.busy_order_ref ? "#FBBF24" : NEON, boxShadow: `0 0 8px ${c.busy_order_ref ? "rgba(251,191,36,0.6)" : "rgba(46,226,123,0.6)"}` }} />
                <span style={{ fontWeight: 700, fontSize: 14 }}>{c.courier_name}</span>
              </span>
              <span style={{ fontSize: 11, color: "#8FA3A3" }}>
                {c.busy_order_ref ? `En course ${c.busy_order_ref.slice(-6)}` : "Disponible"} · {_agoLabel(c.idle_s)}
              </span>
            </button>
          ))}
        </div>
        <button onClick={onClose} data-testid="ops-reassign-cancel" style={cancelBtnStyle}>Annuler</button>
      </div>
    </div>
  );
}

export default function OpsLiveRoomPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [reassignOrder, setReassignOrder] = useState(null);
  const [tab, setTab] = useState("courses");

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/v3/delivery/ops/overview`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` },
      });
      if (r.status === 403) { setError("Accès réservé au compte owner."); return; }
      const d = await r.json();
      if (d.ok) { setData(d); setError(null); }
    } catch (_) { /* garde le snapshot précédent */ }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  if (error) {
    return <div style={pageStyle}><div style={{ ...cardStyle, margin: "40px 14px", textAlign: "center" }}>
      <div style={{ fontSize: 30 }}>🔒</div>
      <p style={{ color: "#8FA3A3", fontSize: 13, marginTop: 8 }}>{error}</p>
      <Link to="/mon-compte" style={backLinkStyle}>← Mon compte</Link>
    </div></div>;
  }
  if (!data) return <div style={pageStyle}><div style={{ padding: 30, color: "#8FA3A3", textAlign: "center" }}>Chargement de la salle ops…</div></div>;

  const couriers = data.couriers || [];
  const orders = data.orders || [];
  // v3.7.3 — Alertes : courses en attente prolongée en premier + clignotement rouge.
  const alertOrders = orders.filter((o) => o.alert);
  const sortedOrders = [...orders].sort((a, b) => (b.alert ? 1 : 0) - (a.alert ? 1 : 0));
  const online = couriers.filter((c) => c.online);
  const busyCount = online.filter((c) => c.busy_order_ref).length;

  return (
    <div style={pageStyle} data-testid="ops-live-room">
      <header style={headerStyle}>
        <Link to="/mon-compte" data-testid="ops-live-back" style={backLinkStyle}>←</Link>
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: NEON, boxShadow: `0 0 10px ${NEON}`, animation: "ops-pulse 1.6s infinite" }} />
            <span style={{ fontSize: 13, color: "#EAFDF3", fontWeight: 900, letterSpacing: 1.5 }}>SALLE OPS LIVE</span>
          </div>
          <div style={{ fontSize: 10.5, color: "#8FA3A3", marginTop: 2 }}>AYA Operations · Contrôle livreurs en temps réel</div>
        </div>
        <Link to="/internal/operations" data-testid="ops-live-to-internal" title="AYA Internal"
          style={{ ...backLinkStyle, fontSize: 12, color: NEON, borderColor: "rgba(46,226,123,0.4)" }}>⌘</Link>
      </header>
      <style>{`
        @keyframes ops-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
        @keyframes ops-alert-blink {
          0%, 100% { border-color: rgba(239,68,68,0.9); box-shadow: 0 0 0 1px rgba(239,68,68,0.55), 0 0 22px rgba(239,68,68,0.45); background: linear-gradient(180deg, rgba(46,12,12,0.9), rgba(20,5,5,0.92)); }
          50% { border-color: rgba(239,68,68,0.25); box-shadow: 0 0 0 0 rgba(239,68,68,0), 0 6px 18px rgba(0,0,0,0.35); background: linear-gradient(180deg, rgba(16,24,19,0.92), rgba(10,15,12,0.94)); }
        }
        @keyframes ops-banner-blink { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
      `}</style>

      {/* Bannière alertes */}
      {alertOrders.length > 0 && (
        <div data-testid="ops-alert-banner" style={{
          margin: "12px 14px 0", padding: "11px 14px", borderRadius: 12,
          background: "rgba(239,68,68,0.14)", border: "1.5px solid rgba(239,68,68,0.6)",
          display: "flex", alignItems: "center", gap: 10,
          animation: "ops-banner-blink 1.1s ease-in-out infinite",
        }}>
          <span style={{ fontSize: 18 }}>🚨</span>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: "#FCA5A5" }}>
            {alertOrders.length} course{alertOrders.length > 1 ? "s" : ""} en souffrance — aucun livreur assigné
          </span>
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, padding: "12px 14px 0" }} data-testid="ops-live-kpis">
        <div style={kpiStyle}><div style={kpiValStyle}>{online.length}</div><div style={kpiLabStyle}>En ligne</div></div>
        <div style={{ ...kpiStyle, borderColor: "rgba(251,191,36,0.25)" }}><div style={{ ...kpiValStyle, color: "#FBBF24" }}>{busyCount}</div><div style={kpiLabStyle}>En course</div></div>
        <div style={{ ...kpiStyle, borderColor: "rgba(96,165,250,0.25)" }}><div style={{ ...kpiValStyle, color: "#60A5FA" }}>{orders.length}</div><div style={kpiLabStyle}>Courses actives</div></div>
      </div>

      {/* Carte */}
      <div style={{ padding: "12px 14px 0" }}>
        <OpsMiniMap couriers={online} orders={orders} />
      </div>

      {/* Onglets */}
      <div style={{ display: "flex", gap: 8, padding: "14px 14px 4px" }}>
        {[["courses", `🛵 Courses (${orders.length})`], ["livreurs", `👥 Livreurs (${online.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`ops-tab-${id}`} style={{
            flex: 1, padding: "10px 12px", borderRadius: 999, fontSize: 12.5, fontWeight: 800, cursor: "pointer",
            background: tab === id ? "rgba(46,226,123,0.12)" : "rgba(255,255,255,0.04)",
            border: tab === id ? `1.5px solid ${NEON}` : "1px solid rgba(255,255,255,0.1)",
            color: tab === id ? NEON : "#8FA3A3",
          }}>{label}</button>
        ))}
      </div>

      {/* Courses actives */}
      {tab === "courses" && (
        <div style={{ padding: "8px 14px 30px", display: "flex", flexDirection: "column", gap: 10 }}>
          {sortedOrders.length === 0 && <div style={{ ...cardStyle, textAlign: "center", color: "#8FA3A3", fontSize: 13 }}>Aucune course active — tout est calme. 🌙</div>}
          {sortedOrders.map((o) => {
            const pill = o.alert
              ? { txt: o.status === "failed" ? `🚨 Échec livreur — ${_waitLabel(o.waiting_s)}` : `🚨 Sans livreur depuis ${_waitLabel(o.waiting_s)}`, color: "#FECACA", bg: "rgba(239,68,68,0.35)" }
              : ORDER_PILLS[o.status] || { txt: o.status, color: "#8FA3A3", bg: "rgba(255,255,255,0.06)" };
            return (
              <div key={o.order_ref}
                style={o.alert ? { ...cardStyle, border: "1.5px solid rgba(239,68,68,0.9)", animation: "ops-alert-blink 1.1s ease-in-out infinite" } : cardStyle}
                data-testid={o.alert ? `ops-order-alert-${o.order_ref}` : `ops-order-${o.order_ref}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "monospace", fontSize: 12, color: "#8FA3A3" }}>{o.order_ref}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 10px", borderRadius: 999, color: pill.color, background: pill.bg }}>{pill.txt}</span>
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: "#EAFDF3", marginTop: 8 }}>
                  {o.commerce_name} <span style={{ color: "#8FA3A3", fontWeight: 400 }}>→</span> {o.drop?.description || o.drop?.address_civ || "client"}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, flexWrap: "wrap", gap: 8 }}>
                  <div style={{ fontSize: 11.5, color: "#8FA3A3" }}>
                    {o.courier_name
                      ? <>🛵 <span style={{ color: "#EAFDF3", fontWeight: 700 }}>{o.courier_name}</span></>
                      : o.current_candidate_name
                        ? <>📡 proposé à <span style={{ color: "#FBBF24", fontWeight: 700 }}>{o.current_candidate_name}</span></>
                        : "📡 recherche en cours"}
                    {" · "}{o.distance_km} km · {o.delivery_fee_fcfa} F
                    {o.reassigned_count > 0 && <span style={{ color: "#FBBF24" }}> · 🔁×{o.reassigned_count}</span>}
                  </div>
                  <button onClick={() => setReassignOrder(o)} data-testid={`ops-reassign-btn-${o.order_ref}`} style={reassignBtnStyle}>🔁 Réassigner</button>
                </div>
              </div>
            );
          })}
          {(data.recent_delivered || []).length > 0 && (
            <>
              <div style={{ fontSize: 11, color: "#5C6E6E", fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", margin: "8px 2px 0" }}>Livrées récemment (2h)</div>
              {data.recent_delivered.map((o) => (
                <div key={o.order_ref} style={{ ...cardStyle, opacity: 0.65, padding: 12 }} data-testid={`ops-delivered-${o.order_ref}`}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12.5, color: "#EAFDF3", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.commerce_name} → {o.drop?.description || "client"}</span>
                    <span style={{ fontSize: 10.5, fontWeight: 800, padding: "3px 10px", borderRadius: 999, color: "#34D399", background: "rgba(52,211,153,0.15)", flexShrink: 0 }}>✓ Livrée</span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Livreurs */}
      {tab === "livreurs" && (
        <div style={{ padding: "8px 14px 30px", display: "flex", flexDirection: "column", gap: 10 }}>
          {online.length === 0 && <div style={{ ...cardStyle, textAlign: "center", color: "#8FA3A3", fontSize: 13 }}>Aucun livreur en ligne.</div>}
          {online.map((c) => (
            <div key={c.session_id} style={cardStyle} data-testid={`ops-courier-${c.session_id.slice(0, 8)}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 11, height: 11, borderRadius: "50%", background: c.busy_order_ref ? "#FBBF24" : NEON, boxShadow: `0 0 10px ${c.busy_order_ref ? "rgba(251,191,36,0.6)" : "rgba(46,226,123,0.6)"}` }} />
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#EAFDF3" }}>{c.courier_name}</span>
                </span>
                <span style={{ fontSize: 11, color: "#8FA3A3" }}>{_agoLabel(c.idle_s)}</span>
              </div>
              <div style={{ fontSize: 11.5, color: c.busy_order_ref ? "#FBBF24" : "#8FA3A3", marginTop: 6 }}>
                {c.busy_order_ref ? `🛵 En course ${c.busy_order_ref}` : "✅ Disponible"}
                {c.company_name && <span style={{ color: "#5C6E6E" }}> · {c.company_name}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {reassignOrder && (
        <ReassignSheet
          order={reassignOrder}
          couriers={couriers}
          onClose={() => setReassignOrder(null)}
          onDone={() => { setReassignOrder(null); load(); }}
        />
      )}
    </div>
  );
}

const pageStyle = { minHeight: "100vh", background: "radial-gradient(ellipse at top, #0E1511 0%, #0A0F0C 65%)", color: "#EAFDF3", fontFamily: "Inter, system-ui, sans-serif" };
const headerStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 14px 10px", borderBottom: "1px solid rgba(46,226,123,0.1)" };
const backLinkStyle = { color: "#8FA3A3", textDecoration: "none", fontSize: 18, padding: "4px 10px", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999 };
const cardStyle = { background: "linear-gradient(180deg, rgba(16,24,19,0.92), rgba(10,15,12,0.94))", border: "1px solid rgba(46,226,123,0.14)", borderRadius: 14, padding: 14, boxShadow: "0 6px 18px rgba(0,0,0,0.35)" };
const kpiStyle = { ...cardStyle, padding: "10px 8px", textAlign: "center", borderColor: "rgba(46,226,123,0.25)" };
const kpiValStyle = { fontSize: 22, fontWeight: 900, color: NEON, fontFeatureSettings: "'tnum'" };
const kpiLabStyle = { fontSize: 9.5, color: "#8FA3A3", textTransform: "uppercase", letterSpacing: 0.6, marginTop: 2 };
const reassignBtnStyle = { padding: "7px 14px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, cursor: "pointer", background: "rgba(46,226,123,0.1)", border: `1px solid rgba(46,226,123,0.45)`, color: NEON };
const sheetOverlayStyle = { position: "fixed", inset: 0, background: "rgba(2,6,8,0.8)", backdropFilter: "blur(6px)", zIndex: 1100, display: "flex", alignItems: "flex-end", justifyContent: "center" };
const sheetStyle = { width: "100%", maxWidth: 520, background: "linear-gradient(180deg, #101813, #0A0F0C)", borderRadius: "20px 20px 0 0", border: "1px solid rgba(46,226,123,0.25)", borderBottom: "none", padding: "14px 18px 26px", boxShadow: "0 -10px 40px rgba(0,0,0,0.6)" };
const candidateBtnStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, width: "100%", padding: "13px 14px", borderRadius: 12, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", color: "#EAFDF3", cursor: "pointer", textAlign: "left" };
const cancelBtnStyle = { width: "100%", marginTop: 14, padding: "12px 16px", borderRadius: 999, background: "transparent", border: "1px solid rgba(255,255,255,0.15)", color: "#8FA3A3", fontSize: 13, fontWeight: 700, cursor: "pointer" };
